import { supabase } from '../lib/supabase';

async function invokeCredentialFunction(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    // supabase-js doesn't parse the response body into `data` on a non-2xx
    // status — the real error message our function returned is only
    // reachable via error.context (the raw Response object).
    let message = error.message || 'Request failed';
    try {
      const body = await error.context?.json();
      if (body?.error) message = body.error;
    } catch (_) { /* response wasn't JSON — fall back to error.message */ }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export const aiTokenService = {
  /**
   * Loads the caller's own safe credential rows (never the encrypted secret)
   * for a given capability: 'text' | 'image' | 'midjourney'.
   */
  async getConnections(capability) {
    try {
      let query = supabase.from('ai_tokens_safe').select('*').order('updated_at', { ascending: false });
      if (capability) query = query.eq('capability', capability);
      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('getConnections error:', error);
      return { data: [], error };
    }
  },

  /** Save (create or replace) the caller's own AI/Image provider credential. */
  async saveProvider({ provider, capability, apiKey, model }) {
    try {
      const data = await invokeCredentialFunction('save-ai-provider', { provider, capability, apiKey, model });
      return { data: data?.token, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Test the caller's own saved AI/Image provider credential. Pass
   * `provider` to test one specific saved text-capability connection
   * (regardless of whether it's the active one) — omit it for the original
   * "test whatever is active" behavior the Image Provider card still uses.
   */
  async testProvider(capability, provider) {
    try {
      const data = await invokeCredentialFunction('test-ai-provider', { capability, provider });
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Disconnect the caller's own AI/Image provider credential. Pass
   * `provider` to remove just that one saved text-capability connection —
   * omit it for the original "clear everything for this capability"
   * behavior the Image Provider card still uses.
   */
  async disconnectProvider(capability, provider) {
    try {
      await invokeCredentialFunction('disconnect-ai-provider', { capability, provider });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  /** Make one of the caller's own already-saved text providers the active one. */
  async activateProvider({ provider, capability }) {
    try {
      const data = await invokeCredentialFunction('activate-ai-provider', { provider, capability });
      return { data: data?.token, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /** Save the caller's own MidJourney (personal Discord) connection. */
  async saveMidjourneyConnection({ discordToken, guildId, channelId }) {
    try {
      const data = await invokeCredentialFunction('save-midjourney-connection', { discordToken, guildId, channelId });
      return { data: data?.connection, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /** Test the caller's own saved MidJourney connection. */
  async testMidjourneyConnection() {
    try {
      const data = await invokeCredentialFunction('test-midjourney-connection', {});
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /** Disconnect the caller's own MidJourney connection. */
  async disconnectMidjourneyConnection() {
    try {
      await invokeCredentialFunction('disconnect-midjourney-connection', {});
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Set (or clear with `null`) the caller's own MidJourney subscription
   * renewal-date reminder. `renewalDate` must be a 'YYYY-MM-DD' string.
   */
  async updateMidjourneyRenewalDate(renewalDate) {
    try {
      const data = await invokeCredentialFunction('update-midjourney-renewal-date', { renewalDate });
      return { data: data?.renewal_date ?? null, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};
